import {
	StudentAPIWrapperSchema,
	StudentsListAPISchema,
	Student,
	transformAPIStudentToUIStudent,
} from "./student.schema";
import { parseAPIResponse } from "./parse-api-response";
import { AuthenticatedAPIClient } from "./authenticated-api";
import { Toast, ErrorMessages } from "./utils";

class StudentAPIService {
	async getStudentByMSSV(mssv: string): Promise<Student> {
		try {
			// `silentErrors`: màn hình tra cứu đã hiện lỗi trong banner đỏ ngay dưới ô
			// nhập, nên toast chỉ nói lại đúng câu đó lần thứ hai. Lỗi vẫn được ném ra
			// bình thường, chỉ không kèm toast.
			const response = await AuthenticatedAPIClient.get(`/students/${encodeURIComponent(mssv)}`, {
				silentErrors: true,
			});

			const validatedData = parseAPIResponse(
				StudentAPIWrapperSchema,
				response,
				"GET /students/:mssv",
			);

			if (!validatedData.success) {
				throw new Error(validatedData.message || "Failed to get student data");
			}

			return transformAPIStudentToUIStudent(validatedData.data);
		} catch (error) {
			// Re-throw auth errors without modification - they're handled by AuthenticatedAPIClient
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			// Không toast ở bất kỳ nhánh nào của hàm này, kể cả lỗi mạng: `student-
			// search.tsx` đã dựng banner đỏ cho mọi lỗi nó bắt được. Hai hàm còn lại
			// trong lớp này thì vẫn toast — chúng được gọi từ những màn không có chỗ
			// hiển thị lỗi riêng.
			if (error instanceof Error) throw error;
			throw new Error("Failed to fetch student information");
		}
	}

	async getAllStudents(
		page: number = 1,
		limit: number = 10,
	): Promise<{
		students: Student[];
		pagination: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
		};
	}> {
		try {
			const response = await AuthenticatedAPIClient.get(`/students?page=${page}&limit=${limit}`);

			const validatedData = parseAPIResponse(StudentsListAPISchema, response, "GET /students");

			if (!validatedData.success) {
				Toast.error(validatedData.message || ErrorMessages.UNKNOWN_ERROR);
				throw new Error(validatedData.message || "Failed to get students list");
			}

			return {
				students: validatedData.data.students.map(transformAPIStudentToUIStudent),
				pagination: validatedData.data.pagination,
			};
		} catch (error) {
			// Re-throw auth errors without modification - they're handled by AuthenticatedAPIClient
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				// Only show toast for network errors, other errors already handled by AuthenticatedAPIClient
				if (error.message.includes("Failed to fetch")) {
					Toast.error(ErrorMessages.NETWORK_ERROR);
				}
				throw error;
			}
			Toast.error(ErrorMessages.UNKNOWN_ERROR);
			throw new Error("Failed to fetch students list");
		}
	}

	async updateStudentByMSSV(
		mssv: string,
		updateData: {
			UpdateUserId: string;
			DaTiepNhan?: boolean;
			ho_so_da_nhan?: string[];
			Note?: string;
		},
	): Promise<Student> {
		// Transform ho_so_da_nhan to GiayTo format expected by API
		const apiUpdateData: any = {
			UpdateUserId: updateData.UpdateUserId,
		};

		if (updateData.DaTiepNhan !== undefined) {
			apiUpdateData.DaTiepNhan = updateData.DaTiepNhan;
		}

		if (updateData.ho_so_da_nhan) {
			apiUpdateData.GiayTo = updateData.ho_so_da_nhan.join(", ");
		}

		if (updateData.Note !== undefined) {
			apiUpdateData.Note = updateData.Note;
		}

		try {
			const response = await AuthenticatedAPIClient.put(
				`/students/${encodeURIComponent(mssv)}`,
				apiUpdateData,
			);

			const validatedData = parseAPIResponse(
				StudentAPIWrapperSchema,
				response,
				"PUT /students/:mssv",
			);

			if (!validatedData.success) {
				Toast.error(validatedData.message || ErrorMessages.UNKNOWN_ERROR);
				throw new Error(validatedData.message || "Failed to update student");
			}

			return transformAPIStudentToUIStudent(validatedData.data);
		} catch (error) {
			// Re-throw auth errors without modification - they're handled by AuthenticatedAPIClient
			if (error instanceof Error && error.message.includes("Authentication required")) {
				throw error;
			}

			if (error instanceof Error) {
				// Only show toast for network errors, other errors already handled by AuthenticatedAPIClient
				if (error.message.includes("Failed to fetch")) {
					Toast.error(ErrorMessages.NETWORK_ERROR);
				}
				throw error;
			}
			Toast.error(ErrorMessages.UNKNOWN_ERROR);
			throw new Error("Failed to update student information");
		}
	}
}

export const studentAPIService = new StudentAPIService();
export { StudentAPIService };
export default studentAPIService;
